import { inject, injectable } from '@servicetitan/react-ioc';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../common/stores/special-settings.store';

const appointmentTimeOptionSettingsKey = 'appointment-time-option-settings';
const jobNameDisplayOptionSettingsKey = 'job-name-display-option-settings';
const proposalNameDisplayOptionSettingsKey = 'proposal-name-display-option-settings';

export enum AppointmentTimeOptionStatus {
    StartAndEndTime = 0,
    ArrivalWindow = 1,
    DateOnly = 2,
}

export interface AppointmentTimeOptionsSettings {
    scheduled: { selection: AppointmentTimeOptionStatus };
    inProgress: { selection: AppointmentTimeOptionStatus };
    completed: { selection: AppointmentTimeOptionStatus };
}

export interface JobNameDisplayOptionsSettings {
    selection: JobNameDisplayOptionStatus;
}

export enum JobNameDisplayOptionStatus {
    JobType = 0,
    ServiceAddress = 1,
    LocationName = 2,
    ServiceDate = 3,
}

export enum ProposalNameDisplayOptionStatus {
    JobType = 0,
    ServiceAddress = 1,
    LocationName = 2,
}

@injectable()
export class SettingsStore {
    @observable appointmentTimeOptions: AppointmentTimeOptionsSettings | undefined = undefined;
    @observable appointmentTimeOptionsError?: string;
    @observable jobNameDisplayOptions: JobNameDisplayOptionStatus | undefined = undefined;
    @observable jobNameDisplayOptionsError?: string;
    @observable proposalNameDisplayOptions: ProposalNameDisplayOptionStatus | undefined = undefined;
    @observable proposalNameDisplayOptionsError?: string;
    constructor(@inject(SpecialSettingsStore) private specialSettings: SpecialSettingsStore) {
        makeObservable(this);
        this.loadAppointmentTimeOptionsSpecialSettings();
        this.loadJobNameDisplayOptionsSpecialSettings();
        this.loadProposalNameDisplayOptionsSpecialSettings();
    }

    @action
    loadAppointmentTimeOptionsSpecialSettings = async () => {
        runInAction(() => {
            this.appointmentTimeOptionsError = undefined;
        });

        const settings = await this.specialSettings.getAsync<AppointmentTimeOptionsSettings>(
            appointmentTimeOptionSettingsKey
        );
        runInAction(() => {
            if (this.specialSettings.failed) {
                this.appointmentTimeOptionsError =
                    'Failed to load appointment time options special settings';
                return;
            }
            this.appointmentTimeOptions = settings;
        });
    };

    @action
    loadJobNameDisplayOptionsSpecialSettings = async () => {
        runInAction(() => {
            this.jobNameDisplayOptionsError = undefined;
        });

        const settings = await this.specialSettings.getAsync<JobNameDisplayOptionsSettings>(
            jobNameDisplayOptionSettingsKey
        );
        runInAction(() => {
            if (this.specialSettings.failed) {
                this.jobNameDisplayOptionsError =
                    'Failed to load job name display options special settings';
                return;
            }
            this.jobNameDisplayOptions = settings?.selection;
        });
    };

    @action
    loadProposalNameDisplayOptionsSpecialSettings = async () => {
        const settings = await this.specialSettings.getAsync<{
            selection: ProposalNameDisplayOptionStatus;
        }>(proposalNameDisplayOptionSettingsKey);
        runInAction(() => {
            if (this.specialSettings.failed) {
                this.proposalNameDisplayOptionsError =
                    'Failed to load proposal name display options special settings';
                return;
            }
            this.proposalNameDisplayOptions = settings?.selection;
        });
    };
}
